import { useEffect, useState } from "react";
import { getFileTypeName, getImagePath } from "../../common/utility/utility";
import { MotifCheckbox } from "@ey-xd/motif-react";

import "./SPFolderUpload.scss";

const FileFetcher = ({ onSelectionChange, sharePointFiles, isSPLoading, onFileSizeCheck }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(isSPLoading);
  const [currentPath, setCurrentPath] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  useEffect(() => {
    setFiles(sharePointFiles);
  },[sharePointFiles]);
  useEffect(() => {setLoading(isSPLoading)},[isSPLoading])
    const fileSizeCheck = selectedFiles && selectedFiles.reduce((acc, file) => acc + (file.size || 0), 0) >= 50 * 1024 * 1024 * 1024;
onFileSizeCheck(fileSizeCheck);
  useEffect(() => {
    // Notify parent of selection changes
    if (onSelectionChange) {
      onSelectionChange({ selectedFiles, selectedFolders });
    }
  }, [selectedFiles, selectedFolders, onSelectionChange]);

  if (loading) return <p>Loading files...</p>;
  function buildFileStructured(data) {
    const structure = { files: {}, subFolders: {} };

    data.forEach((item) => {
      const pathSegments = item.parentReference?.path
        ? item.parentReference.path
            .replace(/.*root:/, "") // Remove the root prefix
            .split("/")
            .filter((segment) => segment) // Split into segments and filter out empty ones
        : []; // Default to an empty array if path is undefined
      const fileName = item.name;

      // Create or traverse the folder structure
      let currentLevel = structure;

      pathSegments.forEach((segment) => {
        // Create the folder if it doesn't exist
        if (!currentLevel.subFolders[segment]) {
          currentLevel.subFolders[segment] = { files: {}, subFolders: {} };
        }
        // Move to the next level
        currentLevel = currentLevel.subFolders[segment];
      });

      // Add the file or folder to the current level
      if (item.folder || item.type === "folder") {
        currentLevel.subFolders[fileName] = {
          files: item.files || {}, // Pass down files if available
          subFolders: item.subFolders || {}, // Pass down subFolders if available
          childCount: item.folder?.childCount || 0,
        };
      } else {
        currentLevel.files[fileName] = {
          downloadUrl: item["@microsoft.graph.downloadUrl"] || item.downloadUrl,
          createdBy: item.createdBy?.user?.displayName || item.createdBy,
          createdByEmail: item.createdBy?.user?.email || item.createdByEmail,
          createdDateTime: item.createdDateTime,
          lastModifiedBy:
            item.lastModifiedBy?.user?.displayName || item.lastModifiedBy,
          lastModifiedByEmail:
            item.lastModifiedBy?.user?.email || item.lastModifiedByEmail,
          lastModifiedDateTime: item.lastModifiedDateTime,
          size: item.size,
          mimeType: item.file?.mimeType || item.mimeType,
          fileId: item.id || item.fileId, // Ensure fileId is assigned correctly
          webUrl: item.webUrl,
          sharedScope: item.shared?.scope || item.sharedScope,
          parentReference:
            item.parentReference || item.folderData?.parentReference,
          name: item.name,
          id: item.id || item.fileId, // Ensure id is assigned correctly
          isSPFile: true, // Mark as SharePoint file
        };
      }
    });

    return structure;
  }
  //const fileStructured = buildFileStructured(files);
  const fileStructured = files && files.length > 0 ? buildFileStructured(files) : { files: {}, subFolders: {} };
  const handleFolderClick = (folderName, folderData) => {
    const updatedPath = [...currentPath, { folderName, folderData }];
    setCurrentPath(updatedPath);
    const updatedFiles = [
      ...Object.entries(folderData.subFolders).map(([name, data]) => {
        return {
          name,
          type: "folder",
          childCount:
            Object.keys(data.files || {}).length +
            Object.keys(data.subFolders || {}).length,
          subFolders: data.subFolders,
          files: data.files,
          folderData: data,
          folderName: name,
        };
      }),
      ...Object.entries(folderData.files).map(([name, data]) => {
        return {
          name,
          type: "file",
          mimeType: data.mimeType,
          size: data.size,
          fileId: data.fileId,
          createdDateTime: data.createdDateTime,
          lastModifiedDateTime: data.lastModifiedDateTime,
          createdBy: data.createdBy,
          createdByEmail: data.createdByEmail,
          lastModifiedBy: data.lastModifiedBy,
          lastModifiedByEmail: data.lastModifiedByEmail,
          downloadUrl: data.downloadUrl,
          webUrl: data.webUrl,
          sharedScope: data.sharedScope || "None",
          folderData: data,
          folderName: folderName,
        };
      }),
    ];
    setFiles(updatedFiles);
  };

  const getAllFilesRecursively = (folder) => {
    let files = Object.values(folder.files || {});
    for (const sub of Object.values(folder.subFolders || {})) {
      files = files.concat(getAllFilesRecursively(sub));
    }
    return files;
  };

  // Compute all files in the current fileStructured for select all
  const allFiles = [
    ...Object.values(fileStructured.files),
    ...Object.values(fileStructured.subFolders).flatMap(getAllFilesRecursively),
  ];

  // Derived value: are all files selected?
  const isAllSelected =
    allFiles.length > 0 &&
    allFiles.every((file) =>
      selectedFiles.some((selected) => selected.fileId === file.fileId)
    );

  // Handler for select all checkbox
  const handleSelectAll = () => {
    if (isAllSelected) {
      // Deselect all
      setSelectedFiles([]);
    } else {
      // Select all files
      setSelectedFiles(allFiles);
    }
  };

  const handleFileCheck = (file) => {
    const isAlreadySelected = selectedFiles.some(
      (selected) => selected.fileId === file.fileId
    );

    if (isAlreadySelected) {
      // Remove file from selectedFiles
      setSelectedFiles((prev) =>
        prev.filter((selected) => selected.fileId !== file.fileId)
      );
    } else {
      // Add file to selectedFiles
      setSelectedFiles((prev) => [...prev, file]);
    }
  };
  const handleFolderCheck = (folderName, filesInFolder) => {
    const isChecked = filesInFolder.every((file) =>
      selectedFiles.some((selected) => selected.fileId === file.fileId)
    );

    if (isChecked) {
      setSelectedFiles((prev) =>
        prev.filter(
          (selected) =>
            !filesInFolder.some((file) => file.fileId === selected.fileId)
        )
      );
    } else {
      setSelectedFiles((prev) => {
        const newFiles = filesInFolder.filter(
          (file) => !prev.some((selected) => selected.fileId === file.fileId)
        );
        return [...prev, ...newFiles];
      });
    }
  };

  function getTotalFilesCount(folderData) {
    let count = Object.keys(folderData.files || {}).length;
    for (const subFolder of Object.values(folderData.subFolders || {})) {
      count += getTotalFilesCount(subFolder);
    }
    return count;
  }
  return (
    <>
      {fileSizeCheck && (
        <div className="storage-limit-warning">
          Maximum file size exceeded. Upload up to 50GB
        </div>
      )}
      {currentPath.length > 0 && (
        <div className="ft-breadcrumb">
          {currentPath.map((path, index) => (
            <span
              key={index}
              onClick={() => {
                const updatedPath = currentPath.slice(0, index + 1);
                setCurrentPath(updatedPath);
                const lastPath = updatedPath[updatedPath.length - 1];
                const updatedFiles = [
                  ...Object.entries(lastPath.folderData.subFolders).map(
                    ([name, data]) => ({
                      name,
                      type: "folder",
                      childCount:
                        Object.keys(data.files || {}).length +
                        Object.keys(data.subFolders || {}).length,
                      subFolders: data.subFolders,
                      files: data.files,
                      folderData: data,
                      folderName: name,
                    })
                  ),
                  ...Object.entries(lastPath.folderData.files).map(
                    ([name, data]) => ({
                      name,
                      type: "file",
                      mimeType: data.mimeType,
                      size: data.size,
                      fileId: data.fileId, // Ensure fileId is included
                      createdDateTime: data.createdDateTime,
                      lastModifiedDateTime: data.lastModifiedDateTime,
                      createdBy: data.createdBy,
                      createdByEmail: data.createdByEmail,
                      lastModifiedBy: data.lastModifiedBy,
                      lastModifiedByEmail: data.lastModifiedByEmail,
                      downloadUrl: data.downloadUrl,
                      webUrl: data.webUrl,
                      sharedScope: data.sharedScope || "None",
                      folderData: data,
                      folderName: name,
                      id: data.id, // Also include id for consistency
                    })
                  ),
                ];
                setFiles(updatedFiles);
              }}
            >
              {path.folderName} {index < currentPath.length - 1 && " > "}
            </span>
          ))}
        </div>
      )}
      <div className="checkbox-align ft-file-item">
        <MotifCheckbox
          id="select-all"
          checked={isAllSelected}
          onChange={handleSelectAll}
        />
        <span className="selectAll-text">Select All</span>
      </div>
      {Object.entries(fileStructured.subFolders).map(
        ([folderName, folderData]) => {
          const filesInFolder = getAllFilesRecursively(folderData);
          const isZip = folderName.endsWith(".zip") ? true : false;
          return (
            <div className="ft-file-item" key={folderName}>
              <MotifCheckbox
                id={folderName}
                checked={filesInFolder.every((file) =>
                  selectedFiles.some(
                    (selected) => selected.fileId === file.fileId
                  )
                )}
                onChange={() => handleFolderCheck(folderName, filesInFolder)}
              />
              <div className="ft-file-icon">
                <img
                  src={
                                isZip
                                  ? require("../../common/images/zip.png")
                                  : require("../../common/images/folder.png")
                              }
                  alt={"Folder icon"}
                />
              </div>
              <div
                className={`ft-file-details`}
                onClick={() => handleFolderClick(folderName, folderData)}
              >
                <div className="ft-file-name cursor-pointer" title={folderName}>
                  <span className="ft-file-name-text">{folderName}</span>
                </div>
                <div className={`ft-file-type`}>
                   <span className="spn-file-type-label">Type: </span>Folder, {getTotalFilesCount(folderData)} File(s)
                </div>  
              </div>
            </div>
          );
        }
      )}
      {Object.entries(fileStructured.files).map(([fileName, fileData]) => (
        <div className="ft-file-item" key={fileName}>
          <MotifCheckbox
            id={fileName}
            checked={selectedFiles.some(
              (selected) => selected.fileId === fileData.fileId
            )}
            onChange={() => handleFileCheck(fileData)}
          />
          <div className="ft-file-icon">
            <img
              src={getImagePath(fileName)}
              alt={`${getFileTypeName(fileName)} icon`}
            />
          </div>
          <div className={`ft-file-details`}>
            <div className="ft-file-name" title={fileName}>
              <span className="ft-file-name-text">{fileName}</span>
            </div>
            <div className={`ft-file-type`}>
             <span className="spn-file-type-label">Type:</span>  {getFileTypeName(fileName)} File
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export default FileFetcher;
